import React from "react";
import "../styles/executive-theme.css";
import { Link, useNavigate } from "react-router-dom";
import { getPlatformSettings } from "../lib/platformSettings";
import { getDefaultDashboardPath } from "../lib/platformSettings";
import {
  ShieldCheck,
  BarChart3,
  ClipboardList,
  TrendingUp,
  Scale,
  ArrowRight,
} from "lucide-react";

const FlowStep = ({ icon: Icon, stage, title, subtitle, path }) => (
  <Link to={path} className="bg-white/80 backdrop-blur-sm border border-white/70 rounded-2xl p-6 shadow-sm hover:shadow-md transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600">
    <p className="text-xs font-semibold tracking-wider text-indigo-700 mb-3">{stage}</p>
    <div className="flex items-center gap-3 mb-4">
      <div className="h-12 w-12 rounded-2xl bg-indigo-50 text-indigo-700 flex items-center justify-center">
        <Icon size={22} aria-hidden="true" />
      </div>

      <h3 className="font-semibold text-slate-900">
        {title}
      </h3>
    </div>

    <p className="text-sm leading-6 text-slate-500">
      {subtitle}
    </p>
  </Link>
);

const QuickCard = ({
  title,
  subtitle,
  path,
}) => (
  <Link
    to={path}
    className="group text-left bg-white/85 backdrop-blur-sm border border-white/70 rounded-2xl p-6 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600"
  >
    <div className="flex items-center justify-between">
      <div>
        <h3 className="font-semibold text-slate-900">
          {title}
        </h3>

        <p className="text-sm text-slate-500 mt-2">
          {subtitle}
        </p>
      </div>

      <ArrowRight
        size={18}
        aria-hidden="true"
        className="text-slate-400 group-hover:text-indigo-600 transition"
      />
    </div>
  </Link>
);

export default function HomePage() {
  const navigate = useNavigate();
  const settings = getPlatformSettings();

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-indigo-50/40 to-white">

      {/* Background Effects */}
      <div className="pointer-events-none absolute inset-0 opacity-60">

        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(99,102,241,0.18),transparent_30%),radial-gradient(circle_at_80%_20%,rgba(124,58,237,0.16),transparent_28%),radial-gradient(circle_at_60%_90%,rgba(59,130,246,0.12),transparent_35%)]" />

        <div className="absolute inset-0 bg-[linear-gradient(rgba(15,23,42,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(15,23,42,0.03)_1px,transparent_1px)] bg-[size:36px_36px]" />

        <div className="absolute right-0 top-24 h-96 w-96 rounded-full bg-indigo-300/20 blur-3xl" />

        <div className="absolute left-20 bottom-10 h-80 w-80 rounded-full bg-blue-300/20 blur-3xl" />

      </div>

      {/* Content */}
      <div className="relative z-10 p-8 space-y-8">

        {/* HERO */}
        <div className="relative overflow-hidden rounded-3xl bg-slate-950 border border-white/10 shadow-2xl">

          {/* Hero FX */}
          <div className="pointer-events-none absolute inset-0">

            <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_20%,rgba(124,58,237,0.45),transparent_30%),radial-gradient(circle_at_35%_15%,rgba(37,99,235,0.28),transparent_35%)]" />

            <div className="absolute inset-0 opacity-30 bg-[linear-gradient(120deg,transparent_20%,rgba(255,255,255,0.12)_45%,transparent_70%)]" />

           <div className="hero-pattern-drift absolute right-0 top-0 h-full w-2/3 opacity-40 bg-[radial-gradient(circle,rgba(255,255,255,0.38)_1px,transparent_1px)] bg-[size:18px_18px]" />

          </div>

          <div className="relative z-10 p-10">

            <p className="text-sm uppercase tracking-[0.28em] text-slate-400 mb-5">
               {settings.organizationName.toUpperCase()}
            </p>

            <h1 className="text-5xl font-bold text-white max-w-5xl leading-tight">
              Turn Digital Risk into Better Capital Decisions
            </h1>

            <p className="text-slate-300 mt-4 max-w-3xl text-xl leading-8">
              Quantify economic exposure, optimize capital allocation and govern investments that strengthen business resilience.
            </p>

            <div className="mt-8 flex gap-4">

              <button
                onClick={() =>
                  navigate(getDefaultDashboardPath())
                }
                className="px-6 py-3 rounded-2xl bg-white text-slate-950 font-medium hover:bg-slate-100 transition"
              >
                Open Workspace
              </button>

              <button
                onClick={() =>
                  navigate("/platform-settings")
                }
                className="px-6 py-3 rounded-2xl border border-white/20 text-white hover:bg-white/10 transition"
              >
                Platform Settings
              </button>

            </div>

            {/* Hero Cards */}
            <div data-testid="hero-capabilities" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 mt-10">

              <div className="bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl p-5 shadow-sm">
                <p className="text-sm text-slate-400">
                  QUANTITATIVE RISK
                </p>

                <p className="text-3xl font-semibold text-white mt-2">
                  FAIR-Based
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl p-5 shadow-sm">
                <p className="text-sm text-slate-400">
                  DECISION INTELLIGENCE
                </p>

                <p className="text-3xl font-semibold text-white mt-2">
                  Capital Optimized
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl p-5 shadow-sm">
                <p className="text-sm text-slate-400">
                  EXECUTIVE EXECUTION
                </p>

                <p className="text-3xl font-semibold text-white mt-2">
                  Management
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl p-5 shadow-sm">
                <p className="text-sm text-slate-400">
                  GOVERNANCE OVERSIGHT
                </p>

                <p className="text-3xl font-semibold text-white mt-2">
                  Board
                </p>
              </div>

            </div>

          </div>
        </div>

        {/* Executive value chain */}
        <section aria-labelledby="executive-value-chain-heading">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-indigo-700">
            Executive orientation
          </p>
          <h2 id="executive-value-chain-heading" className="mt-2 text-2xl font-semibold text-slate-900">
            From digital risk to measurable outcomes
          </h2>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
            Follow the RIGO decision chain to understand the organization’s position and move into the authoritative workspace for each decision.
          </p>

          <div data-testid="value-chain" className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
            <FlowStep
              icon={BarChart3}
              stage="01 · QUANTIFY"
              title="Digital Risk Quantification"
              subtitle="Quantify the economic exposure to digital risk through governed analytical evidence."
              path="/risk-quantification"
            />
            <FlowStep
              icon={ClipboardList}
              stage="02 · ALLOCATE"
              title="Capital Allocation & Investment Efficiency"
              subtitle="Explore where constrained capital can most efficiently improve the digital risk position."
              path="/decision-center"
            />
            <FlowStep
              icon={Scale}
              stage="03 · GOVERN"
              title="Investment Governance"
              subtitle="Propose, authorize and govern investment decisions through their established lifecycle."
              path="/investment-governance"
            />
            <FlowStep
              icon={ShieldCheck}
              stage="04 · STRENGTHEN"
              title="Business Resilience to Digital Risk"
              subtitle="Connect governed investment to the ability to withstand, respond to and recover from material digital-risk events."
              path="/investment-monitoring"
            />
            <FlowStep
              icon={TrendingUp}
              stage="05 · MEASURE"
              title="Outcome Measurement"
              subtitle="Assess whether governed investments are producing their expected business and resilience outcomes."
              path="/benefits-governance"
            />
          </div>
        </section>

        {/* Principal workspaces */}
        <section aria-labelledby="executive-entry-points-heading">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-indigo-700">
            Continue the decision journey
          </p>
          <h2 id="executive-entry-points-heading" className="mt-2 text-2xl font-semibold text-slate-900">
            Principal executive entry points
          </h2>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
            Move from orientation into the dedicated analytical, governance and oversight workspaces without duplicating their authority here.
          </p>

          <div data-testid="executive-entry-points" className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
            <QuickCard title="Portfolio Capital Strategy" subtitle="Evaluate portfolio choices and capital allocation." path="/decision-center" />
            <QuickCard title="Risk Quantification" subtitle="Examine authoritative digital risk evidence." path="/risk-quantification" />
            <QuickCard title="Investment Governance" subtitle="Govern investment proposals and authorizations." path="/investment-governance" />
            <QuickCard title="Program Control Tower" subtitle="Monitor authorized programs and execution." path="/investment-monitoring" />
            <QuickCard title="Outcome Measurement" subtitle="Review value realization and expected outcomes." path="/benefits-governance" />
            <QuickCard title="Executive Dashboard" subtitle="Review the detailed management position." path="/executive-dashboard" />
            <QuickCard title="Board Dashboard" subtitle="Review the dedicated Board oversight view." path="/board-dashboard" />
          </div>
        </section>

      </div>
    </div>
  );
}
